import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Tabs,
  Tab,
  Chip,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Dashboard,
  People,
  Gavel,
  AttachMoney,
  ShoppingCart,
  Star,
  CheckCircle,
  Block,
  Person,
  Report,
  Visibility,
  VerifiedUser,
  Analytics
} from '@mui/icons-material';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  blockedUsers: number;
  totalTransactions: number;
  totalRevenue: number;
  activeDisputes: number;
  resolvedDisputes: number;
  popularCategories: Array<{
    name: string;
    count: number;
    revenue: number;
  }>;
  recentActivity: Array<{
    id: string;
    type: 'user_registration' | 'transaction' | 'dispute' | 'user_blocked';
    description: string;
    timestamp: Date;
    severity: 'low' | 'medium' | 'high';
  }>;
}

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  status: 'active' | 'blocked' | 'pending_verification';
  verificationStatus: {
    email: boolean;
    phone: boolean;
    identity: boolean;
  };
  registrationDate: Date;
  lastActivity: Date;
  totalTransactions: number;
  totalSpent: number;
  rating: number;
  reports: number;
  isVerified: boolean;
  riskScore: number;
}

interface Dispute {
  id: string;
  type: 'damage' | 'non_return' | 'payment' | 'communication' | 'other';
  status: 'open' | 'investigating' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  complainant: {
    id: string;
    name: string;
    email: string;
  };
  respondent: {
    id: string;
    name: string;
    email: string;
  };
  bookingId: string;
  dressTitle: string;
  amount: number;
  description: string;
  evidence: Array<{
    type: 'photo' | 'message' | 'document';
    url: string;
    description: string;
  }>;
  createdAt: Date;
  updatedAt: Date;
  assignedTo?: string;
  resolution?: string;
}

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openDisputeDialog, setOpenDisputeDialog] = useState(false);
  const [userFilter, setUserFilter] = useState<'all' | 'active' | 'blocked' | 'pending'>('all');
  const [disputeFilter, setDisputeFilter] = useState<'all' | 'open' | 'investigating' | 'resolved'>('all');

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    
    // Simulation du chargement des données
    setTimeout(() => {
      setStats({
        totalUsers: 1247,
        activeUsers: 1156,
        blockedUsers: 23,
        totalTransactions: 3456,
        totalRevenue: 125430,
        activeDisputes: 12,
        resolvedDisputes: 234,
        popularCategories: [
          { name: 'Robes de soirée', count: 456, revenue: 45600 },
          { name: 'Robes de cocktail', count: 234, revenue: 23400 },
          { name: 'Robes de mariée', count: 123, revenue: 36900 },
          { name: 'Accessoires', count: 89, revenue: 8900 }
        ],
        recentActivity: [
          {
            id: '1',
            type: 'user_registration',
            description: 'Nouvel utilisateur: Marie Dubois',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
            severity: 'low'
          },
          {
            id: '2',
            type: 'dispute',
            description: 'Litige ouvert: Robe endommagée',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
            severity: 'high'
          },
          {
            id: '3',
            type: 'transaction',
            description: 'Transaction: 150€ - Robe de soirée',
            timestamp: new Date(Date.now() - 30 * 60 * 1000),
            severity: 'low'
          }
        ]
      });

      setUsers([
        {
          id: '1',
          name: 'Marie Dubois',
          email: 'marie.dubois@email.com',
          phone: '+33 1 23 45 67 89',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150',
          status: 'active',
          verificationStatus: { email: true, phone: true, identity: true },
          registrationDate: new Date('2024-01-15'),
          lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000),
          totalTransactions: 12,
          totalSpent: 1200,
          rating: 4.8,
          reports: 0,
          isVerified: true,
          riskScore: 15
        },
        {
          id: '2',
          name: 'Jean Martin',
          email: 'jean.martin@email.com',
          phone: '+33 1 98 76 54 32',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
          status: 'blocked',
          verificationStatus: { email: true, phone: false, identity: false },
          registrationDate: new Date('2024-02-01'),
          lastActivity: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          totalTransactions: 3,
          totalSpent: 300,
          rating: 2.1,
          reports: 5,
          isVerified: false,
          riskScore: 85
        },
        {
          id: '3',
          name: 'Sophie Laurent',
          email: 'sophie.laurent@email.com',
          phone: '+33 1 55 44 33 22',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
          status: 'pending_verification',
          verificationStatus: { email: true, phone: true, identity: false },
          registrationDate: new Date('2024-03-01'),
          lastActivity: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          totalTransactions: 0,
          totalSpent: 0,
          rating: 0,
          reports: 0,
          isVerified: false,
          riskScore: 45
        }
      ]);

      setDisputes([
        {
          id: '1',
          type: 'damage',
          status: 'open',
          priority: 'high',
          complainant: {
            id: '1',
            name: 'Marie Dubois',
            email: 'marie.dubois@email.com'
          },
          respondent: {
            id: '2',
            name: 'Jean Martin',
            email: 'jean.martin@email.com'
          },
          bookingId: 'BOOK_123',
          dressTitle: 'Robe de soirée élégante',
          amount: 150,
          description: 'La robe a été rendue avec une tache importante sur le devant',
          evidence: [
            {
              type: 'photo',
              url: '/evidence/damage_photo.jpg',
              description: 'Photo de la tache'
            }
          ],
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
        },
        {
          id: '2',
          type: 'non_return',
          status: 'investigating',
          priority: 'urgent',
          complainant: {
            id: '3',
            name: 'Sophie Laurent',
            email: 'sophie.laurent@email.com'
          },
          respondent: {
            id: '4',
            name: 'Pierre Durand',
            email: 'pierre.durand@email.com'
          },
          bookingId: 'BOOK_124',
          dressTitle: 'Robe de cocktail moderne',
          amount: 200,
          description: 'La robe n\'a pas été retournée dans les délais',
          evidence: [
            {
              type: 'message',
              url: '/evidence/messages.txt',
              description: 'Échanges de messages'
            }
          ],
          createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
          updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          assignedTo: 'admin_1'
        }
      ]);

      setLoading(false);
    }, 1500);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'blocked': return 'error';
      case 'pending_verification': return 'warning';
      case 'open': return 'error';
      case 'investigating': return 'warning';
      case 'resolved': return 'success';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const handleUserAction = (userId: string, action: 'block' | 'unblock' | 'verify') => {
    setUsers(prevUsers => 
      prevUsers.map(user => {
        if (user.id === userId) {
          switch (action) {
            case 'block':
              return { ...user, status: 'blocked' as const };
            case 'unblock':
              return { ...user, status: 'active' as const };
            case 'verify':
              return { ...user, isVerified: true, verificationStatus: { ...user.verificationStatus, identity: true } };
            default:
              return user;
          }
        }
        return user;
      })
    );
  };

  const handleDisputeAction = (disputeId: string, action: 'assign' | 'resolve' | 'close') => {
    setDisputes(prevDisputes => 
      prevDisputes.map(dispute => {
        if (dispute.id === disputeId) {
          switch (action) {
            case 'assign':
              return { ...dispute, status: 'investigating' as const, assignedTo: 'admin_1' };
            case 'resolve':
              return { ...dispute, status: 'resolved' as const, resolution: 'Litige résolu en faveur du plaignant' };
            case 'close':
              return { ...dispute, status: 'closed' as const };
            default:
              return dispute;
          }
        }
        return dispute;
      })
    );
  };

  const filteredUsers = users.filter(user => {
    if (userFilter === 'all') return true;
    return user.status === userFilter;
  });

  const filteredDisputes = disputes.filter(dispute => {
    if (disputeFilter === 'all') return true;
    return dispute.status === disputeFilter;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ color: '#a5816d', mb: 3 }}>
        <Dashboard sx={{ mr: 1, verticalAlign: 'middle' }} />
        Administration
      </Typography>

      {/* Statistiques générales */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <People sx={{ fontSize: 40, color: '#2196f3', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalUsers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Utilisateurs total
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoney sx={{ fontSize: 40, color: '#4caf50', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalRevenue.toLocaleString()}€
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Chiffre d'affaires
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ShoppingCart sx={{ fontSize: 40, color: '#ff9800', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalTransactions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Transactions
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Gavel sx={{ fontSize: 40, color: '#f44336', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.activeDisputes}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Litiges actifs
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Onglets principaux */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Dashboard" icon={<Dashboard />} />
          <Tab label="Utilisateurs" icon={<People />} />
          <Tab label="Litiges" icon={<Gavel />} />
          <Tab label="Statistiques" icon={<Analytics />} />
        </Tabs>
      </Paper>

      {/* Contenu des onglets */}
      {activeTab === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Activité récente
          </Typography>
          <List>
            {stats?.recentActivity.map((activity) => (
              <ListItem key={activity.id}>
                <ListItemIcon>
                  <Chip 
                    label={activity.type} 
                    color={getSeverityColor(activity.severity) as any}
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={activity.description}
                  secondary={activity.timestamp.toLocaleString('fr-FR')}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Gestion des utilisateurs
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Filtrer</InputLabel>
              <Select
                value={userFilter}
                label="Filtrer"
                onChange={(e) => setUserFilter(e.target.value as any)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="active">Actifs</MenuItem>
                <MenuItem value="blocked">Bloqués</MenuItem>
                <MenuItem value="pending">En attente</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Vérification</TableCell>
                  <TableCell>Score de risque</TableCell>
                  <TableCell>Transactions</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar src={user.avatar} sx={{ mr: 2 }} />
                        <Box>
                          <Typography variant="subtitle2">{user.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.status} 
                        color={getStatusColor(user.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {user.verificationStatus.email && <CheckCircle color="success" />}
                        {user.verificationStatus.phone && <CheckCircle color="success" />}
                        {user.verificationStatus.identity && <VerifiedUser color="success" />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={user.riskScore} 
                          sx={{ width: 60, mr: 1 }}
                          color={user.riskScore > 70 ? 'error' : user.riskScore > 40 ? 'warning' : 'success'}
                        />
                        <Typography variant="caption">{user.riskScore}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.totalTransactions} ({user.totalSpent}€)
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Voir détails">
                          <IconButton size="small" onClick={() => {
                            setSelectedUser(user);
                            setOpenUserDialog(true);
                          }}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {user.status === 'active' ? (
                          <Tooltip title="Bloquer">
                            <IconButton size="small" onClick={() => handleUserAction(user.id, 'block')}>
                              <Block color="error" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Débloquer">
                            <IconButton size="small" onClick={() => handleUserAction(user.id, 'unblock')}>
                              <CheckCircle color="success" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Gestion des litiges
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Filtrer</InputLabel>
              <Select
                value={disputeFilter}
                label="Filtrer"
                onChange={(e) => setDisputeFilter(e.target.value as any)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="open">Ouverts</MenuItem>
                <MenuItem value="investigating">En cours</MenuItem>
                <MenuItem value="resolved">Résolus</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Litige</TableCell>
                  <TableCell>Priorité</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Montant</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDisputes.map((dispute) => (
                  <TableRow key={dispute.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{dispute.dressTitle}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dispute.complainant.name} vs {dispute.respondent.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={dispute.priority} 
                        color={getPriorityColor(dispute.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={dispute.status} 
                        color={getStatusColor(dispute.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {dispute.amount}€
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {dispute.createdAt.toLocaleDateString('fr-FR')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Voir détails">
                          <IconButton size="small" onClick={() => {
                            setSelectedDispute(dispute);
                            setOpenDisputeDialog(true);
                          }}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {dispute.status === 'open' && (
                          <Tooltip title="Assigner">
                            <IconButton size="small" onClick={() => handleDisputeAction(dispute.id, 'assign')}>
                              <Person />
                            </IconButton>
                          </Tooltip>
                        )}
                        {dispute.status === 'investigating' && (
                          <Tooltip title="Résoudre">
                            <IconButton size="small" onClick={() => handleDisputeAction(dispute.id, 'resolve')}>
                              <CheckCircle color="success" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Catégories populaires
          </Typography>
          <Grid container spacing={2}>
            {stats?.popularCategories.map((category, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {category.name}
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {category.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.revenue.toLocaleString()}€ de revenus
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Dialog de détails utilisateur */}
      <Dialog open={openUserDialog} onClose={() => setOpenUserDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Détails de l'utilisateur
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar src={selectedUser.avatar} sx={{ width: 80, height: 80, mr: 3 }} />
                <Box>
                  <Typography variant="h5">{selectedUser.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedUser.email}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedUser.phone}</Typography>
                </Box>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Statut</Typography>
                  <Chip label={selectedUser.status} color={getStatusColor(selectedUser.status) as any} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Score de risque</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={selectedUser.riskScore} 
                    color={selectedUser.riskScore > 70 ? 'error' : 'warning'}
                  />
                  <Typography variant="caption">{selectedUser.riskScore}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Transactions</Typography>
                  <Typography variant="body1">{selectedUser.totalTransactions}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Montant total</Typography>
                  <Typography variant="body1">{selectedUser.totalSpent}€</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Note moyenne</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Star sx={{ color: '#ffc107', mr: 0.5 }} />
                    <Typography variant="body1">{selectedUser.rating}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Signalements</Typography>
                  <Typography variant="body1">{selectedUser.reports}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUserDialog(false)}>Fermer</Button>
          {selectedUser && (
            <>
              {selectedUser.status === 'active' ? (
                <Button color="error" onClick={() => {
                  handleUserAction(selectedUser.id, 'block');
                  setOpenUserDialog(false);
                }}>
                  Bloquer
                </Button>
              ) : (
                <Button color="success" onClick={() => {
                  handleUserAction(selectedUser.id, 'unblock');
                  setOpenUserDialog(false);
                }}>
                  Débloquer
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de détails litige */}
      <Dialog open={openDisputeDialog} onClose={() => setOpenDisputeDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Détails du litige
        </DialogTitle>
        <DialogContent>
          {selectedDispute && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedDispute.dressTitle}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Type</Typography>
                  <Chip label={selectedDispute.type} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Priorité</Typography>
                  <Chip 
                    label={selectedDispute.priority} 
                    color={getPriorityColor(selectedDispute.priority) as any}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Statut</Typography>
                  <Chip 
                    label={selectedDispute.status} 
                    color={getStatusColor(selectedDispute.status) as any}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Montant</Typography>
                  <Typography variant="h6">{selectedDispute.amount}€</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedDispute.description}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                Parties impliquées
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Plaignant:</strong> {selectedDispute.complainant.name} ({selectedDispute.complainant.email})
                </Typography>
                <Typography variant="body2">
                  <strong>Défendeur:</strong> {selectedDispute.respondent.name} ({selectedDispute.respondent.email})
                </Typography>
              </Box>

              {selectedDispute.evidence.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Preuves
                  </Typography>
                  <List>
                    {selectedDispute.evidence.map((evidence, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Report />
                        </ListItemIcon>
                        <ListItemText
                          primary={evidence.description}
                          secondary={evidence.type}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDisputeDialog(false)}>Fermer</Button>
          {selectedDispute && selectedDispute.status === 'open' && (
            <Button 
              color="primary" 
              onClick={() => {
                handleDisputeAction(selectedDispute.id, 'assign');
                setOpenDisputeDialog(false);
              }}
            >
              Assigner
            </Button>
          )}
          {selectedDispute && selectedDispute.status === 'investigating' && (
            <Button 
              color="success" 
              onClick={() => {
                handleDisputeAction(selectedDispute.id, 'resolve');
                setOpenDisputeDialog(false);
              }}
            >
              Résoudre
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
